/**
 * Newsletter EmailJS Integration
 * Handles newsletter submissions via EmailJS with consent checking
 */

import emailjs from '@emailjs/browser';

/**
 * Submit newsletter signup
 * @param {Object} formData - { name, email, school }
 * @returns {Promise} EmailJS response
 * @throws {Error} 'NO_CONSENT' if marketing consent not given
 */
export async function submitNewsletter(formData) {
  // Check marketing consent
  if (!window.cookieConsent || !window.cookieConsent.hasConsent('marketing')) {
    showConsentDialog('marketing');
    throw new Error('NO_CONSENT');
  }

  // Prepare template parameters
  const templateParams = {
    user_name: formData.name || '',
    user_email: formData.email || '',
    user_school: formData.school || 'Niet opgegeven',
    contact_message: '', // Empty for newsletter (used by contact form)
    timestamp: new Date().toLocaleString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    page_url: window.location.href,
    browser_info: navigator.userAgent
  };

  // Send via EmailJS
  try {
    const response = await emailjs.send(
      import.meta.env.VITE_EMAILJS_SERVICE_ID,
      import.meta.env.VITE_EMAILJS_TEMPLATE_NEWSLETTER,
      templateParams,
      import.meta.env.VITE_EMAILJS_PUBLIC_KEY
    );
    return response;
  } catch (error) {
    console.error('EmailJS Error:', error);
    throw error;
  }
}

/**
 * Show friendly consent dialog when marketing consent missing
 * @param {string} category - Consent category needed
 */
function showConsentDialog(category) {
  // Check if dialog already exists
  if (document.querySelector('.consent-dialog')) return;

  const dialog = document.createElement('div');
  dialog.className = 'consent-dialog';
  dialog.innerHTML = `
    <div class="consent-dialog-overlay"></div>
    <div class="consent-dialog-content">
      <h3>Marketing cookies vereist</h3>
      <p>Voor nieuwsbrieven hebben we je toestemming nodig voor marketing cookies. Je gegevens worden alleen gebruikt voor het versturen van onze nieuwsbrief.</p>
      <div class="consent-dialog-buttons">
        <button class="consent-btn consent-btn-primary" onclick="window.cookieConsent.openModal(); this.closest('.consent-dialog').remove();">
          Cookie Instellingen
        </button>
        <button class="consent-btn consent-btn-secondary" onclick="this.closest('.consent-dialog').remove();">
          Annuleren
        </button>
      </div>
    </div>
  `;

  // Add styles inline for portability
  const style = document.createElement('style');
  style.textContent = `
    .consent-dialog {
      position: fixed;
      inset: 0;
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .consent-dialog-overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
    }
    .consent-dialog-content {
      position: relative;
      background: white;
      padding: 2rem;
      border-radius: 8px;
      max-width: 500px;
      margin: 1rem;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    }
    .consent-dialog-content h3 {
      margin: 0 0 1rem 0;
      color: #1a1a1a;
      font-size: 1.5rem;
    }
    .consent-dialog-content p {
      margin: 0 0 1.5rem 0;
      color: #6b7280;
      line-height: 1.6;
    }
    .consent-dialog-buttons {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
    }
    .consent-btn {
      flex: 1;
      min-width: 120px;
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      cursor: pointer;
      transition: all 0.2s;
    }
    .consent-btn-primary {
      background: #d97706;
      color: white;
    }
    .consent-btn-primary:hover {
      background: #b45309;
    }
    .consent-btn-secondary {
      background: #f3f4f6;
      color: #1a1a1a;
    }
    .consent-btn-secondary:hover {
      background: #e5e7eb;
    }
  `;

  document.head.appendChild(style);
  document.body.appendChild(dialog);

  // Close on overlay click
  dialog.querySelector('.consent-dialog-overlay').addEventListener('click', () => {
    dialog.remove();
  });
}
